import React, { useEffect, useState } from 'react';
import api from './api';
import AyaLogo from './AyaLogo';
import { PROJECT_STATUS_BADGE_CLASS, PROJECT_STATUS_LABELS, projectCode, formatDate, formatMontant, userDisplayName } from './helpers';

const STATUTS = ['Tous', 'Active', 'Delayed', 'Completed', 'Cancelled'];
const STATUT_LABELS = { Tous: 'Tous', Active: 'Actifs', Delayed: 'En retard', Completed: 'Terminés', Cancelled: 'Annulés' };

/**
 * Rapport d'avancement du portefeuille : une ligne par projet, imprimable.
 * Le rapport détaillé d'un projet (synthèse, répartition des tâches, budget)
 * reste dans l'onglet « Rapport » du projet.
 */
function Reports() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [jalons, setJalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statut, setStatut] = useState('Tous');

  useEffect(() => {
    Promise.all([api.get('/projects'), api.get('/users'), api.get('/tasks')])
      .then(([p, u, t]) => {
        setProjects(p.data);
        setUsers(u.data);
        setJalons(t.data.filter((x) => x.est_jalon));
      })
      .catch(() => setError('Impossible de charger les rapports.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p>Chargement...</p>;
  if (error) return <div className="error-message">{error}</div>;

  const visibles = statut === 'Tous' ? projects : projects.filter((p) => p.status === statut);
  const chef = (p) => {
    const u = users.find((x) => x.id === p.project_manager_id);
    return u ? userDisplayName(u) : <span style={{ color: 'var(--text-muted)' }}>À désigner</span>;
  };
  // Jalon à montrer pour un projet : le premier jalon en retard s'il y en a
  // un (c'est l'alerte), sinon le prochain jalon non atteint (c'est le cap).
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const jalonDuProjet = (p) => {
    const siens = jalons
      .filter((j) => j.project_id === p.id && j.status !== 'Done')
      .sort((a, b) => String(a.due_date || '9').localeCompare(String(b.due_date || '9')));
    if (siens.length === 0) {
      const atteints = jalons.filter((j) => j.project_id === p.id && j.status === 'Done').length;
      return atteints ? { texte: 'Tous les jalons atteints', etat: 'ok', total: atteints } : null;
    }
    const enRetard = siens.find((j) => j.due_date && j.due_date < aujourdhui);
    if (enRetard) return { ...enRetard, etat: 'retard' };
    return { ...siens[0], etat: 'a_venir' };
  };

  const compter = (s) => (s === 'Tous' ? projects.length : projects.filter((p) => p.status === s).length);

  const moyenne = visibles.length
    ? Math.round(visibles.reduce((acc, p) => acc + (p.progress_percent || 0), 0) / visibles.length)
    : 0;

  const dateEdition = formatDate(aujourdhui);

  return (
    <div className="fade-in rapport">
      {/* En-tête réservé à l'impression : sans lui, le PDF sortait sans titre
          ni date — une liste de projets nue, impossible à classer. */}
      <div className="rapport-entete rapport-entete-impression">
        <AyaLogo className="rapport-logo" />
        <div>
          <div className="rapport-surtitre">AYA Gold &amp; Silver — Zgounder</div>
          <div className="rapport-titre">Rapport d’avancement du portefeuille</div>
          <div className="rapport-soustitre">
            {STATUT_LABELS[statut]} · {visibles.length} projet{visibles.length > 1 ? 's' : ''} ·
            avancement moyen {moyenne} % (tâches terminées) · Édité le {dateEdition}
          </div>
        </div>
      </div>

      <div className="page-header-row" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: 0 }}>
            État du portefeuille au {dateEdition} — {visibles.length} projet{visibles.length > 1 ? 's' : ''},
            avancement moyen {moyenne} % (tâches terminées).
          </p>
          <div className="filter-pills" style={{ marginTop: 10, marginBottom: 0 }}>
            {STATUTS.map((s) => (
              <button key={s} className={`filter-pill ${statut === s ? 'active' : ''}`} onClick={() => setStatut(s)}>
                {STATUT_LABELS[s]} ({compter(s)})
              </button>
            ))}
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => window.print()}>Imprimer / exporter en PDF</button>
      </div>

      <div className="table-container">
        <table className="data-table rapports-tableau">
          <thead>
            <tr>
              <th>Réf.</th>
              <th>Projet</th>
              <th>Statut</th>
              <th>Chef de projet</th>
              <th>Échéance</th>
              <th>Jalon</th>
              <th>Tâches terminées</th>
              <th>Budget</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((p) => (
              <tr key={p.id}>
                <td style={{ whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{projectCode(p.id)}</td>
                <td>
                  <div style={{ fontWeight: 600 }}>{p.name}</div>
                  {p.blocked_count > 0 && (
                    <div style={{ fontSize: 11.5, color: 'var(--danger)' }}>
                      {p.blocked_count} tâche{p.blocked_count > 1 ? 's' : ''} bloquée{p.blocked_count > 1 ? 's' : ''}
                    </div>
                  )}
                </td>
                <td>
                  <span className={`badge ${PROJECT_STATUS_BADGE_CLASS[p.status] || 'badge-active'}`}>
                    {PROJECT_STATUS_LABELS[p.status] || p.status}
                  </span>
                </td>
                <td>{chef(p)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{p.end_date ? formatDate(p.end_date) : <span style={{ color: 'var(--text-muted)' }}>Non fixée</span>}</td>
                <td style={{ minWidth: 180 }}>
                  {(() => {
                    const j = jalonDuProjet(p);
                    if (!j) return <span style={{ color: 'var(--text-muted)' }}>Aucun jalon</span>;
                    if (j.etat === 'ok') return <span style={{ color: 'var(--success)' }}>◆ {j.texte} ({j.total})</span>;
                    return (
                      <div>
                        <div style={{ color: j.etat === 'retard' ? 'var(--danger)' : 'var(--text-primary)', fontWeight: 500 }}>
                          ◆ {j.name}
                        </div>
                        <div style={{ fontSize: 11, color: j.etat === 'retard' ? 'var(--danger)' : 'var(--text-muted)' }}>
                          {j.etat === 'retard' ? 'En retard depuis le ' : 'Prévu le '}{j.due_date ? formatDate(j.due_date) : '—'}
                        </div>
                      </div>
                    );
                  })()}
                </td>
                <td>
                  <div className="mini-barre">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${p.progress_percent || 0}%` }} />
                    </div>
                    <span style={{ fontSize: 12, minWidth: 34, textAlign: 'right' }}>{p.progress_percent || 0}%</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {p.task_done_count ?? 0} / {p.task_count ?? 0} tâches
                  </div>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {p.budget_allocated
                    ? `${p.budget_consumed_percent || 0} % · ${formatMontant(p.budget_spent || 0)} / ${formatMontant(p.budget_allocated)}`
                    : <span style={{ color: 'var(--text-muted)' }}>Non renseigné</span>}
                </td>
              </tr>
            ))}
            {visibles.length === 0 && (
              <tr><td colSpan={8} className="empty-state">Aucun projet dans cette catégorie.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="rapport-pied">
        VIGIE · Rapport d’avancement du portefeuille · AYA Gold &amp; Silver, Zgounder · Édité le {dateEdition}
      </div>
    </div>
  );
}

export default Reports;
